// Stealth Angel rebuild: slideshow for sas-promo-hero. Scroll-snap track with arrows, dots, and autoplay that
// pauses on hover, on keyboard focus, when the tab is hidden, or by the pause button; no autoplay with reduced motion.

class SasSlideshow extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false; // set by the pause button; hover and focus pause only while they last
  #hold = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.track.children];
    if (this.slides.length < 2) return;
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.#index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.#index + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot))));
    this.pauseButton = this.querySelector('[data-pause]');
    this.pauseButton?.addEventListener('click', () => this.#setPaused(!this.#paused));

    let settle;
    this.track.addEventListener('scroll', () => {
      clearTimeout(settle);
      settle = setTimeout(() => this.#sync(Math.round(this.track.scrollLeft / this.track.clientWidth)), 80);
    }, { passive: true });

    const hold = (on) => { this.#hold += on ? 1 : -1; this.#schedule(); };
    this.addEventListener('mouseenter', () => hold(true));
    this.addEventListener('mouseleave', () => hold(false));
    this.addEventListener('focusin', () => hold(true));
    this.addEventListener('focusout', () => hold(false));
    document.addEventListener('visibilitychange', () => this.#schedule());

    this.#sync(0);
    this.#schedule();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  go(index) {
    const next = (index + this.slides.length) % this.slides.length;
    this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: this.reduced ? 'auto' : 'smooth' });
    this.#sync(next);
  }

  #sync(index) {
    this.#index = Math.max(0, Math.min(index, this.slides.length - 1));
    this.slides.forEach((slide, i) => {
      // Off-screen slides leave the tab order, so keyboard users only meet the visible slide's link.
      slide.inert = i !== this.#index;
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.#index)));
  }

  #setPaused(paused) {
    this.#paused = paused;
    this.pauseButton?.setAttribute('aria-pressed', String(paused));
    this.pauseButton?.setAttribute('aria-label', paused ? this.pauseButton.dataset.playLabel || 'Play' : this.pauseButton.dataset.pauseLabel || 'Pause');
    this.#schedule();
  }

  #schedule() {
    clearInterval(this.#timer);
    const seconds = Number(this.dataset.autoplay) || 0;
    if (!seconds || this.reduced || this.#paused || this.#hold > 0 || document.hidden) return;
    this.#timer = setInterval(() => this.go(this.#index + 1), seconds * 1000);
  }
}

if (!customElements.get('sas-slideshow')) customElements.define('sas-slideshow', SasSlideshow);
